/**
 * @author       grady@zumper.com (Grady Kuhnline)
 * @copyright    Copyright (c) 2018, Zumper
 * @description  Location schema
 */

import { EMPTY_BOUNDS } from '@zcode/nr/es/shared/bizos/location'
import type { Location as LocationType } from '@zcode/state-type'

export interface LocationWithVacation extends LocationType {
  readonly vacationUrl: string | null
}

export const EMPTY_LOCATION = {
  box: EMPTY_BOUNDS,
  country: null,
  cityName: null,
  destinationName: null,
  hoodName: null,
  lat: null,
  lng: null,
  loading: false,
  missingLocation: false,
  stateName: null,
  url: null,
  vacationUrl: null,
  zoom: null,
} as const satisfies LocationWithVacation

export type Location = {
  readonly [Key in Exclude<keyof LocationWithVacation, 'box' | 'loading'>]:
    | LocationWithVacation[Key]
    | undefined
} & {
  readonly box: NonNullable<LocationWithVacation['box']>
  readonly loading?: never
}

export const Location = {
  fromJS: <T extends Partial<Location>>(
    data: T
  ): Location & {
    readonly [Key in Exclude<keyof Location, 'box' | 'loading'>]: T[Key]
  } & (T['box'] extends {
      readonly minLat: number
      readonly minLng: number
      readonly maxLat: number
      readonly maxLng: number
    }
      ? Pick<T, 'box'>
      : unknown) => ({
    box: { ...EMPTY_BOUNDS, ...data.box },
    country: data.country,
    cityName: data.cityName,
    destinationName: data.destinationName,
    hoodName: data.hoodName,
    lat: data.lat,
    lng: data.lng,
    missingLocation: data.missingLocation,
    stateName: data.stateName,
    url: data.url,
    vacationUrl: data.vacationUrl,
    zoom: data.zoom,
  }),
}
